import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  BOOKING_SELECT,
  fetchPendingMechanicRequests,
  fetchQuoteStatuses,
  fetchTechnicians,
  hydrateReceptionFlags,
  subscribeToBookings,
} from '../lib/bookingData';
import { displayName } from '../lib/format';
import { describeJob, HANDOVER_TRACKING_SINCE } from '../lib/jobStage';
import { isResaleBooking } from '../lib/resaleDesk';
import { supabase } from '../lib/supabase';

const OPEN_STATUSES = 'pending,confirmed,checked_in,in_progress';
const RECENT_DAYS = 60;
// Bookings stream in live; quotes and mechanic requests are not in the realtime
// publication, so they (and time-based flags) refresh on this timer.
const REFRESH_MS = { today: 60 * 1000, all: 5 * 60 * 1000 };
const LIVE_DEBOUNCE_MS = 400;

function bookingsQuery(scope) {
  const query = supabase.from('bookings').select(BOOKING_SELECT);
  if (scope === 'today') {
    const recent = new Date(Date.now() - RECENT_DAYS * 24 * 60 * 60 * 1000);
    const since = recent > HANDOVER_TRACKING_SINCE ? recent : HANDOVER_TRACKING_SINCE;
    return query
      .or(`status.in.(${OPEN_STATUSES}),completed_at.gte.${since.toISOString()}`)
      .order('scheduled_at', { ascending: true });
  }
  return query.order('scheduled_at', { ascending: false });
}

/**
 * Workshop jobs described for the current user.
 * scope 'today' = open jobs plus recent finished ones;
 * scope 'all'   = every workshop booking.
 * Both update live when any booking changes.
 */
export function useJobs(scope = 'all') {
  const { profile } = useAuth();
  const [rows, setRows] = useState([]);
  const [quoteStatuses, setQuoteStatuses] = useState(() => new Map());
  const [requests, setRequests] = useState(() => new Map());
  const [live, setLive] = useState(false);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [now, setNow] = useState(() => new Date());

  const load = useCallback(async () => {
    const [{ data, error: queryError }, quotes, techs, pending] = await Promise.all([
      bookingsQuery(scope),
      fetchQuoteStatuses().catch(() => new Map()),
      fetchTechnicians().catch(() => []),
      fetchPendingMechanicRequests().catch(() => new Map()),
    ]);
    if (queryError) {
      setError(queryError.message);
      setLoading(false);
      return;
    }
    const workshop = await hydrateReceptionFlags(
      (data || []).filter((row) => !isResaleBooking(row)),
    );
    setRows(workshop);
    setQuoteStatuses(quotes);
    setTechnicians(techs);
    setRequests(pending);
    setError('');
    setNow(new Date());
    setLoading(false);
  }, [scope]);

  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    let alive = true;
    let debounce = null;
    const run = () => {
      if (alive) loadRef.current();
    };
    run();
    const timer = setInterval(run, REFRESH_MS[scope] || REFRESH_MS.all);
    let unsubscribe = () => {};
    try {
      unsubscribe = subscribeToBookings(
        () => {
          clearTimeout(debounce);
          debounce = setTimeout(run, LIVE_DEBOUNCE_MS);
        },
        undefined,
        (connected) => {
          if (alive) setLive(connected);
        },
      );
    } catch {
      setLive(false);
    }
    return () => {
      alive = false;
      clearInterval(timer);
      clearTimeout(debounce);
      unsubscribe();
    };
  }, [scope]);

  const techNames = useMemo(
    () => new Map(technicians.map((tech) => [tech.id, displayName(tech)])),
    [technicians],
  );

  const jobs = useMemo(
    () =>
      rows.map((row) => {
        const job = describeJob(row, {
          role: profile?.role,
          userId: profile?.id,
          quoteStatus: quoteStatuses.get(row.id) || null,
          requests: requests.get(row.id),
          now,
        });
        return {
          ...job,
          row,
          mechanicName: job.assignedTechnicianId
            ? techNames.get(job.assignedTechnicianId) || null
            : null,
        };
      }),
    [rows, profile?.role, profile?.id, quoteStatuses, requests, techNames, now],
  );

  return { jobs, loading, error, now, live, reload: load };
}
